import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { couponService } from '../services/couponService';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from '../components/AppHeader';
import { Coupon } from '../types';
import { Edit } from '@mui/icons-material';

interface EditCouponFormData {
  number: number;
  sellerName?: string;
}

const editCouponSchema: yup.ObjectSchema<EditCouponFormData> = yup.object({
  number: yup.number()
    .required('Número do cupom é obrigatório')
    .min(1, 'Número deve ser maior que zero'),
  sellerName: yup.string().optional()
});

  const EditMyCoupon: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string>('');
  const [coupon, setCoupon] = useState<Coupon | null>(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<EditCouponFormData>({
    resolver: yupResolver(editCouponSchema),
    defaultValues: {
      number: 0,
      sellerName: ''
    }
  });

  useEffect(() => {
    const fetchCoupon = async () => {
      if (!id) {
        console.error('ID do cupom não fornecido');
        return;
      }
      
      try {
        console.log('🔍 Buscando cupom:', id);
        setLoadingData(true);
        const data = await couponService.getMyCouponById(id);
        console.log('📦 Cupom encontrado:', data);
        setCoupon(data);
        
        // Preencher formulário com dados do cupom
        reset({
          number: data.number,
          sellerName: data.sellerName || ''
        });
      } catch (error: any) {
        console.error('❌ Erro ao carregar cupom:', error);
        console.error('Detalhes do erro:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        setError('Erro ao carregar dados do cupom');
      } finally {
        setLoadingData(false);
      }
    };

    fetchCoupon();
  }, [id, reset]);

  const onSubmit: SubmitHandler<EditCouponFormData> = async (data) => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');

      await couponService.updateMyCoupon(id, {
        number: data.number,
        sellerName: data.sellerName
      });

      navigate('/user/my-coupons');
    } catch (err: any) {
      console.error('Erro ao atualizar cupom:', err);
      
      if (err.response?.status === 409) {
        setError('Este número de cupom já existe na campanha');
      } else if (err.response?.status === 400) {
        setError(err.response.data.message || 'Dados inválidos fornecidos');
      } else if (err.response?.status === 404) {
        setError('Cupom não encontrado');
      } else {
        setError(err.response?.data?.message || 'Erro ao atualizar cupom');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Carregando cupom...
        </Typography>
      </Box>
    );
  }

  if (!coupon) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error">
          Cupom não encontrado
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <AppHeader 
        title="Editar Meu Cupom"
        subtitle="Atualize as informações do seu cupom"
        icon={<Edit />}
        backTo="/user/my-coupons"
      />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Editar Meu Cupom
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Edite as informações do seu cupom de participação.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Informações do Cupom
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Campanha
                </Typography>
                <Typography variant="body1">
                  {coupon.campaign?.name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Loja
                </Typography>
                <Typography variant="body1">
                  {coupon.store?.name}
                </Typography>
              </Box>

              <Controller
                name="number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Número do Cupom"
                    type="number"
                    error={!!errors.number}
                    helperText={errors.number?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="sellerName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Vendedor"
                    error={!!errors.sellerName}
                    helperText={errors.sellerName?.message}
                    fullWidth
                  />
                )}
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </>
  );
}; 
export default EditMyCoupon;